import { Shield, Target, Eye, Heart, Award, Users, Lightbulb, Lock } from "lucide-react";

export default function AboutPage() {
  const values = [
    {
      icon: Heart,
      title: "Patient First",
      description: "Every decision we make starts with the question: How does this benefit patients?",
    },
    {
      icon: Award,
      title: "Excellence",
      description: "We hold ourselves to the highest standards of quality and regulatory compliance.",
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "We continuously push the boundaries of what's possible in healthcare technology.",
    },
    {
      icon: Users,
      title: "Collaboration",
      description: "We work closely with healthcare professionals to ensure our solutions meet real needs.",
    },
  ];

  return (
    <div className="noise-bg min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden" data-testid="about-hero-section">
        {/* Background glow */}
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px]"></div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <span className="caption mb-4 block" data-testid="about-label">About Us</span>
            <h1 className="heading-1 text-white mb-6" data-testid="about-title">
              Guarding Health Through <span className="gradient-text">Innovation</span>
            </h1>
            <p className="body-large" data-testid="about-description">
              BastionGuard Medical Technologies Private Limited is a healthcare innovation company 
              dedicated to developing medical devices and healthcare applications that protect and 
              improve lives.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding" data-testid="mission-vision-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Mission */}
            <div className="card-glass" data-testid="mission-card">
              <div className="icon-glow mb-6">
                <Target className="w-6 h-6" />
              </div>
              <h2 className="text-2xl font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Our Mission</h2>
              <p className="body-base">
                To bridge the gap between advanced medical technology and patient care by developing 
                innovative medical devices and digital health solutions that are accessible, reliable, 
                and designed with the end-user in mind.
              </p>
            </div>

            {/* Vision */}
            <div className="card-glass" data-testid="vision-card">
              <div className="icon-glow mb-6">
                <Eye className="w-6 h-6" />
              </div>
              <h2 className="text-2xl font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Our Vision</h2>
              <p className="body-base">
                To be a leader in healthcare innovation, recognized for our commitment to 
                quality, patient safety, and technological excellence. We envision a world where 
                advanced healthcare is accessible to everyone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="section-padding relative overflow-hidden" data-testid="story-section">
        {/* Background glow */}
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[100px]"></div>
        
        <div className="container-custom relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            <div className="flex-1 w-full">
              <div className="relative">
                <div className="aspect-video glass-card rounded-3xl flex items-center justify-center">
                  <div className="w-24 h-24 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center animate-glow-pulse">
                    <Shield className="w-12 h-12 text-cyan-400" />
                  </div>
                </div>
                <div className="absolute -bottom-6 -right-6 w-48 h-48 border border-cyan-500/20 rounded-3xl -z-10"></div>
              </div>
            </div>
            <div className="flex-1">
              <span className="caption mb-4 block" data-testid="story-label">Our Story</span>
              <h2 className="heading-2 text-white mb-6" data-testid="story-title">
                Built on Expertise, Driven by Purpose
              </h2>
              <div className="space-y-4 body-base" data-testid="story-content">
                <p>
                  BastionGuard Medical Technologies was founded with a singular purpose: to make 
                  healthcare safer and more accessible through innovation. Our name reflects our 
                  commitment—like a bastion, we stand as a protective force in healthcare.
                </p>
                <p>
                  Our team combines deep expertise in medical device engineering, healthcare IT, 
                  and regulatory affairs. This multidisciplinary approach allows us to develop 
                  solutions that are not only technologically advanced but also compliant with 
                  healthcare standards.
                </p>
                <p>
                  As we continue to grow, we remain focused on our core mission: protecting lives 
                  through medical innovation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding" data-testid="values-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="caption mb-4 block" data-testid="values-label">Our Values</span>
            <h2 className="heading-2 text-white" data-testid="values-title">
              What Guides Us
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={value.title}
                className="card-glass text-center"
                data-testid={`value-card-${index}`}
              >
                <div className="icon-glow mx-auto mb-6">
                  <value.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-white text-lg mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{value.title}</h3>
                <p className="body-base text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance - DPDP Only */}
      <section className="section-padding" data-testid="compliance-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="caption mb-4 block" data-testid="compliance-label">Compliance</span>
            <h2 className="heading-2 text-white mb-4" data-testid="compliance-title">
              Data Protection Compliance
            </h2>
            <p className="body-large max-w-2xl mx-auto" data-testid="compliance-description">
              We are committed to protecting personal data in accordance with India's 
              Digital Personal Data Protection Act.
            </p>
          </div>

          <div className="max-w-md mx-auto">
            <div
              className="card-glass text-center"
              data-testid="standard-0"
            >
              <div className="icon-glow mx-auto mb-6 animate-glow-pulse">
                <Lock className="w-6 h-6" />
              </div>
              <p className="font-bold text-white text-2xl mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>DPDP Act 2023</p>
              <p className="text-cyan-400 mb-2">Digital Personal Data Protection</p>
              <p className="text-sm text-slate-500">Government of India</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
